import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, SearchIcon, PlusIcon, CheckCircleIcon, ClockIcon, EditIcon, TrashIcon, UserIcon, PackageIcon, AlertTriangleIcon, XIcon, CheckIcon, BanIcon, TruckIcon, HistoryIcon, PrinterIcon, AlertCircleIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Order, OrderLine, Product } from '../types';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { generateCotizacionPDF } from '../components/CotizacionPDF';
// Promotion type for local use
interface ActivePromo {
  productId: string;
  tipo: 'oferta' | 'impulso';
  descuentoPct: number;
  precioOferta: number;
  notas: string;
}
// ── Moved outside component to avoid TDZ error ──────────────────────────────
type HeaderForm = {
  numeroPedido: string;
  farmacia: string;
  vendedor: string;
  fecha: string;
  estado: Order['estado'];
};
const emptyHeader: HeaderForm = {
  numeroPedido: '',
  farmacia: '',
  vendedor: '',
  fecha: new Date().toISOString().split('T')[0],
  estado: 'pendiente'
};
const STATUS_CYCLE: Order['estado'][] = ['pendiente', 'procesando', 'empacado', 'enviado', 'entregado', 'cancelado'];
// ─────────────────────────────────────────────────────────────────────────────
export function PedidosPage() {
  const {
    orders,
    refreshData,
    updateOrderStatus,
    clients,
    users
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const isRepartidor = user?.permisos?.dashboard_entregas === true && !isAdmin;
  const isCliente = user?.rolNombre === 'Cliente';
  const canEditOrders = !isCliente && (user?.permisos?.pedidos_editar !== false || isAdmin);
  const [inventory, setInventory] = useState<Product[]>([]);
  const [activePromos, setActivePromos] = useState<ActivePromo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(isRepartidor ? 'empacado' : 'all');
  // Repartidor tab: 'pendientes' (empacado+enviado) | 'entregados' | 'todos'
  const [repartidorTab, setRepartidorTab] = useState<'pendientes' | 'entregados' | 'todos'>('pendientes');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [headerForm, setHeaderForm] = useState<HeaderForm>(emptyHeader);
  const [lineas, setLineas] = useState<OrderLine[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOrderId, setSavedOrderId] = useState<string | null>(null);
  // Track saved cotización to prevent duplicates across Print + Save
  const [savedCotizacionId, setSavedCotizacionId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Build dynamic lists from real data — only actual field vendedores
  const vendedores = users.filter(u => {
    if (!u.activo) return false;
    if (!u.permisos?.['new-visit']) return false;
    // Exclude admins and repartidores — they have new-visit but aren't vendedores
    if (u.rolNombre === 'Administrador') return false;
    if (u.permisos?.users === true) return false;
    if (u.permisos?.dashboard_entregas === true && u.rolNombre !== 'Vendedor') return false;
    return true;
  }).map(u => u.nombre);
  const farmacias = clients.map(c => c.nombre).sort();
  useEffect(() => {
    loadProducts();
    loadPromotions();
  }, []);
  const refreshProductData = useCallback(() => {
    loadProducts();
    loadPromotions();
  }, []);
  useRealtimeRefresh(['products', 'product_promotions'], refreshProductData);
  const mapProduct = (row: any): Product => ({
    id: row.id,
    nombre: row.nombre,
    sku: row.sku,
    stock: row.stock,
    precio: row.precio,
    subTotal: row.sub_total || 0,
    itbis: row.itbis || 0,
    proveedor: row.proveedor || '',
    estado: row.estado || 'activo',
    stockMinimo: row.stock_minimo ?? 5
  });
  const loadProducts = async () => {
    setIsLoading(true);
    try {
      // EMERGENCY: Single query with limit instead of while(hasMore) loop
      const {
        data,
        error
      } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
      if (!error && data) {
        setInventory(data.map(mapProduct));
      }
    } finally {
      setIsLoading(false);
    }
  };
  const loadPromotions = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('product_promotions').select('product_id, tipo, descuento_pct, precio_oferta, notas').eq('activo', true);
      if (data) {
        setActivePromos(data.map((r: any) => ({
          productId: r.product_id,
          tipo: r.tipo,
          descuentoPct: r.descuento_pct || 0,
          precioOferta: r.precio_oferta || 0,
          notas: r.notas || ''
        })));
      }
    } catch {}
  };
  const updateStatus = async (order: Order, newStatus: Order['estado']) => {
    // Optimistic update — UI changes instantly
    await updateOrderStatus(order.id, newStatus);
    addActivityLog('Cambió estado pedido', `Pedido: ${order.numeroPedido} | ${order.estado} → ${newStatus} | Farmacia: ${order.farmacia}`);
    setSavedOrderId(order.id);
    setTimeout(() => setSavedOrderId(null), 1500);
    // If cancelling, restore stock in background (don't block UI)
    if (newStatus === 'cancelado' && order.estado !== 'cancelado' && order.lineas) {
      Promise.all((order.lineas || []).map(async l => {
        const {
          data: prod
        } = await supabaseAdmin.from('products').select('stock').eq('id', l.productoId).single();
        if (prod) {
          await supabaseAdmin.from('products').update({
            stock: prod.stock + l.cantidad
          }).eq('id', l.productoId);
        }
      })).then(() => loadProducts());
    }
  };
  const cycleStatus = async (order: Order) => {
    const currentIndex = STATUS_CYCLE.indexOf(order.estado);
    const nextStatus = STATUS_CYCLE[(currentIndex + 1) % STATUS_CYCLE.length];
    updateStatus(order, nextStatus);
  };
  // Vendedores de campo (con permiso new-visit) solo ven sus propios pedidos
  // Almacén y otros roles con permiso pedidos ven todos los pedidos
  const isFieldVendor = !isAdmin && !isRepartidor && !isCliente && user?.permisos?.['new-visit'] === true;
  // Repartidor global counts (from ALL orders, before filtering)
  const repartidorPendientesCount = orders.filter(o => o.estado === 'empacado' || o.estado === 'enviado').length;
  const repartidorEntregadosCount = orders.filter(o => o.estado === 'entregado').length;
  const filteredOrders = orders.filter(order => {
    // Cliente only sees their own orders (matched by farmacia = user nombre)
    if (isCliente && order.farmacia !== user?.nombre) return false;
    if (isRepartidor) {
      // Repartidor can see empacado, enviado, AND entregado orders
      if (order.estado !== 'empacado' && order.estado !== 'enviado' && order.estado !== 'entregado') return false;
      // Apply repartidor tab filter
      if (repartidorTab === 'pendientes' && order.estado !== 'empacado' && order.estado !== 'enviado') return false;
      if (repartidorTab === 'entregados' && order.estado !== 'entregado') return false;
      // Apply search filter
      const matchesSearch = searchQuery.trim() === '' || order.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || order.numeroPedido.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    }
    if (isFieldVendor && order.vendedor !== user?.nombre) return false;
    const matchesSearch = order.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || order.numeroPedido.toLowerCase().includes(searchQuery.toLowerCase()) || order.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || order.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedOrders,
    requestSort,
    sortConfig
  } = useSortableData(filteredOrders, {
    numeroPedido: o => o.numeroPedido,
    farmacia: o => o.farmacia,
    vendedor: o => o.vendedor,
    fecha: o => new Date(o.fecha).getTime(),
    items: o => o.items,
    total: o => o.total,
    estado: o => o.estado
  });
  // Default sort by date descending if no sort config
  const finalOrders = sortConfig ? sortedOrders : [...sortedOrders].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPedidos = finalOrders.length;
  const pedidosPendientes = finalOrders.filter(o => o.estado === 'pendiente' || o.estado === 'procesando').length;
  const pedidosCompletados = finalOrders.filter(o => o.estado === 'entregado').length;
  const montoTotal = finalOrders.reduce((sum, o) => sum + o.total, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: Order['estado']) => {
    switch (status) {
      case 'entregado':
        return <Badge variant="success" data-id="element-3752">Entregado</Badge>;
      case 'enviado':
        return <Badge variant="info" data-id="element-3753">Enviado</Badge>;
      case 'empacado':
        return <Badge variant="info" data-id="element-3754">Empacado</Badge>;
      case 'procesando':
        return <Badge variant="warning" data-id="element-3755">Procesando</Badge>;
      case 'cancelado':
        return <Badge variant="danger" data-id="element-3756">Cancelado</Badge>;
      default:
        return <Badge variant="default" data-id="element-3757">Pendiente</Badge>;
    }
  };
  // Product search results for adding to order
  const productResults = (() => {
    // Filter out products already added to the order
    const available = inventory.filter(p => (!p.estado || p.estado === 'activo') && !lineas.some(l => l.productoId === p.id));
    let results: Product[];
    // If search is empty, show first 20 products when modal is open
    if (productSearch.trim().length === 0) {
      results = isModalOpen ? available : [];
    } else {
      // Filter by search term
      results = available.filter(p => p.nombre.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase()));
    }
    // Sort: promoted products first, then alphabetical
    return results.sort((a, b) => {
      const aPromo = activePromos.find(pr => pr.productId === a.id);
      const bPromo = activePromos.find(pr => pr.productId === b.id);
      if (aPromo && !bPromo) return -1;
      if (!aPromo && bPromo) return 1;
      return a.nombre.localeCompare(b.nombre);
    }).slice(0, 20);
  })();
  const addProductToOrder = (product: Product) => {
    // Apply promotion discount to price if available
    const promo = activePromos.find(pr => pr.productId === product.id);
    let finalPrice = product.precio;
    if (promo) {
      if (promo.precioOferta > 0) {
        finalPrice = promo.precioOferta;
      } else if (promo.descuentoPct > 0) {
        finalPrice = Math.round(finalPrice * (1 - promo.descuentoPct / 100) * 100) / 100;
      }
    }
    setLineas(prev => {
      const existing = prev.find(l => l.productoId === product.id);
      if (existing) {
        return prev.map(l => l.productoId === product.id ? {
          ...l,
          cantidad: l.cantidad + 1
        } : l);
      }
      return [...prev, {
        productoId: product.id,
        nombre: product.nombre,
        sku: product.sku,
        cantidad: 1,
        precio: finalPrice,
        itbis: product.itbis || 0,
        stockDisponible: product.stock
      }];
    });
    setProductSearch('');
  };
  const updateLineCantidad = (productoId: string, cantidad: number) => {
    // Allow empty/0 without removing — only the × button removes products
    const safeCantidad = isNaN(cantidad) || cantidad < 0 ? 0 : cantidad;
    setLineas(prev => prev.map(l => l.productoId === productoId ? {
      ...l,
      cantidad: safeCantidad
    } : l));
  };
  const removeLinea = (productoId: string) => {
    setLineas(prev => prev.filter(l => l.productoId !== productoId));
  };
  const orderSubtotal = lineas.reduce((sum, l) => sum + l.cantidad * l.precio, 0);
  const orderItbisTotal = lineas.reduce((sum, l) => sum + l.cantidad * (l.itbis || 0), 0);
  const orderTotal = orderSubtotal + orderItbisTotal;
  const hasStockIssues = lineas.some(l => l.cantidad > l.stockDisponible);
  const totalPages = Math.ceil(finalOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = finalOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const openAddModal = () => {
    setEditingOrder(null);
    setSaveError(null);
    setSavedCotizacionId(null);
    const currentYear = new Date().getFullYear();
    const yearPrefix = `PED-${currentYear}-`;
    const existing = orders.map(o => {
      const match = o.numeroPedido.match(new RegExp(`^PED-${currentYear}-(\\d+)$`));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const nextPedido = `${yearPrefix}${String(nextNum).padStart(3, '0')}`;
    setHeaderForm({
      ...emptyHeader,
      numeroPedido: nextPedido,
      farmacia: isCliente ? user?.nombre || '' : '',
      vendedor: isCliente ? user?.nombre || '' : ''
    });
    setLineas([]);
    setProductSearch('');
    setIsModalOpen(true);
  };
  const openEditModal = async (order: Order) => {
    setEditingOrder(order);
    setSaveError(null);
    setHeaderForm({
      numeroPedido: order.numeroPedido,
      farmacia: order.farmacia,
      vendedor: order.vendedor,
      fecha: new Date(order.fecha).toISOString().split('T')[0],
      estado: order.estado
    });
    setProductSearch('');
    setIsModalOpen(true);
    // Use order.lineas if available, otherwise fetch from DB
    let orderLineas: OrderLine[] = order.lineas && order.lineas.length > 0 ? [...order.lineas] : [];
    if (orderLineas.length === 0) {
      // Fetch order_lines directly from Supabase
      try {
        const {
          data
        } = await supabaseAdmin.from('order_lines').select('*').eq('order_id', order.id);
        if (data && data.length > 0) {
          orderLineas = data.map((l: any): OrderLine => ({
            productoId: l.producto_id,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            itbis: l.itbis || 0,
            stockDisponible: l.stock_disponible
          }));
        }
      } catch (err) {
        console.error('Error fetching order_lines:', err);
      }
    }
    // Update stockDisponible from current inventory for accurate indicators
    const updatedLineas = orderLineas.map(l => {
      const currentProduct = inventory.find(p => p.id === l.productoId);
      return {
        ...l,
        stockDisponible: currentProduct ? currentProduct.stock + l.cantidad : l.stockDisponible
      };
    });
    setLineas(updatedLineas);
  };
  const handleSave = async () => {
    if (!headerForm.numeroPedido || lineas.length === 0) return;
    if (!headerForm.farmacia) {
      setSaveError('Debes seleccionar una farmacia.');
      return;
    }
    if (!headerForm.vendedor && !isCliente) {
      setSaveError('Debes seleccionar un vendedor.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
      const orderPayload = {
        numero_pedido: headerForm.numeroPedido,
        farmacia: headerForm.farmacia,
        vendedor: headerForm.vendedor,
        fecha: new Date(headerForm.fecha).toISOString(),
        items: totalItems,
        total: orderTotal,
        estado: headerForm.estado
      };
      let orderLinesOk = true;
      let cotizacionOk = true;
      if (editingOrder) {
        const {
          error: updateError
        } = await supabaseAdmin.from('orders').update(orderPayload).eq('id', editingOrder.id);
        if (updateError) {
          console.error('Error actualizando pedido:', updateError);
          setSaveError('Error al actualizar el pedido: ' + updateError.message);
          return;
        }
        const {
          error: deleteError
        } = await supabaseAdmin.from('order_lines').delete().eq('order_id', editingOrder.id);
        if (deleteError) {
          console.error('Error eliminando líneas:', deleteError.message, deleteError.details, deleteError.hint);
          setSaveError('Error al actualizar las líneas del pedido: ' + deleteError.message + (deleteError.hint ? ' — Hint: ' + deleteError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
          return;
        }
        if (lineas.length > 0) {
          const editLinesPayload = lineas.map(l => ({
            order_id: editingOrder.id,
            producto_id: l.productoId,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            stock_disponible: l.stockDisponible
          }));
          console.log('[DEBUG] Insertando order_lines (edit):', JSON.stringify(editLinesPayload));
          const {
            data: editInsertedLines,
            error: linesError
          } = await supabaseAdmin.from('order_lines').insert(editLinesPayload).select();
          if (linesError) {
            orderLinesOk = false;
            console.error('Error insertando líneas:', linesError.message, linesError.details, linesError.hint);
            setSaveError('Error al guardar las líneas del pedido (order_lines): ' + linesError.message + (linesError.hint ? ' — Hint: ' + linesError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
            // Don't return — the order was updated, just lines failed
          } else {
            console.log('[DEBUG] order_lines (edit) insertadas OK:', editInsertedLines?.length, 'filas');
          }
        }
      } else {
        const {
          data: newOrder,
          error: insertError
        } = await supabaseAdmin.from('orders').insert(orderPayload).select().single();
        if (insertError || !newOrder) {
          console.error('Error creando pedido:', insertError);
          setSaveError('Error al crear el pedido: ' + (insertError?.message || 'No se recibió respuesta del servidor'));
          return;
        }
        if (lineas.length > 0) {
          const orderLinesPayload = lineas.map(l => ({
            order_id: newOrder.id,
            producto_id: l.productoId,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            stock_disponible: l.stockDisponible
          }));
          console.log('[DEBUG] Insertando order_lines:', JSON.stringify(orderLinesPayload));
          const {
            data: insertedLines,
            error: linesError
          } = await supabaseAdmin.from('order_lines').insert(orderLinesPayload).select();
          if (linesError) {
            orderLinesOk = false;
            console.error('Error insertando líneas del nuevo pedido:', linesError.message, linesError.details, linesError.hint);
            setSaveError('El pedido se creó pero hubo un error al guardar los productos (order_lines): ' + linesError.message + (linesError.hint ? ' — Hint: ' + linesError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
            // Don't return — the order was created, just lines failed
          } else {
            console.log('[DEBUG] order_lines insertadas OK:', insertedLines?.length, 'filas');
          }
          // ── Reduce stock for each product in the new order ──────────────
          await Promise.all(lineas.map(async l => {
            const newStock = Math.max(0, l.stockDisponible - l.cantidad);
            await supabaseAdmin.from('products').update({
              stock: newStock
            }).eq('id', l.productoId);
          }));
          // Refresh local inventory so stock indicators update immediately
          await loadProducts();
        }
        // ── Guardar cotización automáticamente al crear pedido (skip if already saved by Print) ─────────
        if (!savedCotizacionId) {
          try {
            const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
            const cotizacionPayload = {
              numero_cotizacion: headerForm.numeroPedido,
              farmacia: headerForm.farmacia,
              vendedor: headerForm.vendedor,
              fecha: new Date(headerForm.fecha).toISOString(),
              items: totalItems,
              total: orderTotal
            };
            const {
              data: newCotizacion,
              error: cotError
            } = await supabaseAdmin.from('cotizaciones').insert(cotizacionPayload).select().single();
            if (cotError) {
              console.error('Error guardando cotización al crear pedido:', cotError.message);
              cotizacionOk = false;
            }
            if (newCotizacion) {
              setSavedCotizacionId(newCotizacion.id);
              if (lineas.length > 0) {
                const cotLinesPayload = lineas.map(l => ({
                  cotizacion_id: newCotizacion.id,
                  producto_id: l.productoId,
                  nombre: l.nombre,
                  sku: l.sku,
                  cantidad: l.cantidad,
                  precio: l.precio,
                  stock_disponible: l.stockDisponible
                }));
                const {
                  error: cotLinesError
                } = await supabaseAdmin.from('cotizacion_lines').insert(cotLinesPayload).select();
                if (cotLinesError) {
                  console.error('Error guardando líneas de cotización:', cotLinesError.message);
                  cotizacionOk = false;
                }
              }
            }
          } catch (cotErr: any) {
            console.error('Error al guardar cotización:', cotErr);
            cotizacionOk = false;
          }
        }
      }
      await refreshData();
      // Only close modal and clear error if everything saved successfully
      if (orderLinesOk && cotizacionOk) {
        addActivityLog(editingOrder ? 'Editó pedido' : 'Creó pedido', `Pedido: ${headerForm.numeroPedido} | Farmacia: ${headerForm.farmacia} | Total: RD${orderTotal.toFixed(2)}`);
        setIsModalOpen(false);
        setSaveError(null);
      }
      // If there's a saveError (from cotizacion or order_lines), keep modal open
    } catch (err: any) {
      console.error('Error inesperado guardando pedido:', err);
      setSaveError('Error inesperado: ' + (err?.message || 'Intente nuevamente'));
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingOrder) return;
    // Restore stock if order was not cancelled/delivered
    if (editingOrder.estado !== 'cancelado' && editingOrder.lineas) {
      await Promise.all((editingOrder.lineas || []).map(async l => {
        const {
          data: prod
        } = await supabaseAdmin.from('products').select('stock').eq('id', l.productoId).single();
        if (prod) {
          await supabaseAdmin.from('products').update({
            stock: prod.stock + l.cantidad
          }).eq('id', l.productoId);
        }
      }));
    }
    await supabaseAdmin.from('orders').delete().eq('id', editingOrder.id);
    addActivityLog('Eliminó pedido', `Pedido: ${editingOrder.numeroPedido} | Farmacia: ${editingOrder.farmacia}`);
    await refreshData();
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  const getStockIndicator = (linea: OrderLine) => {
    const stockRestante = linea.stockDisponible - linea.cantidad;
    if (linea.stockDisponible === 0) return {
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      label: 'Sin stock',
      icon: '✗'
    };
    if (linea.cantidad > linea.stockDisponible) return {
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      label: `Faltan ${linea.cantidad - linea.stockDisponible} uds`,
      icon: '✗'
    };
    if (stockRestante === 0) return {
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      label: 'Quedarán 0 uds',
      icon: '⚠'
    };
    if (stockRestante <= linea.stockDisponible * 0.2) return {
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      label: `Quedarán ${stockRestante} uds`,
      icon: '⚠'
    };
    return {
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      label: `Quedarán ${stockRestante} uds`,
      icon: '✓'
    };
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-3758">
      <PageHeader title={isRepartidor ? 'Entregas' : isCliente ? 'Mis Pedidos' : 'Pedidos'} subtitle={isRepartidor ? 'Pedidos empacados listos para entregar' : isCliente ? 'Tus órdenes de compra' : 'Gestión y seguimiento de órdenes de compra'} icon={isRepartidor ? <TruckIcon className="w-6 h-6" data-id="element-3760" /> : <ShoppingCartIcon className="w-6 h-6" data-id="element-3761" />} data-id="element-3759">
        {!isRepartidor && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3763" />} onClick={openAddModal} data-id="element-3762">
            Nuevo Pedido
          </Button>}
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3764">
        <StatsCard title={isRepartidor ? 'Por Entregar' : 'Total Pedidos'} value={totalPedidos} icon={isRepartidor ? <PackageIcon className="w-5 h-5" data-id="element-3766" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-3767" />} variant={isRepartidor ? 'warning' : 'primary'} data-id="element-3765" />
        <StatsCard title={isRepartidor ? 'Entregados Hoy' : 'En Proceso'} value={isRepartidor ? orders.filter(o => o.estado === 'entregado' && new Date(o.fecha).toDateString() === new Date().toDateString()).length : pedidosPendientes} icon={isRepartidor ? <CheckCircleIcon className="w-5 h-5" data-id="element-3769" /> : <ClockIcon className="w-5 h-5" data-id="element-3770" />} variant={isRepartidor ? 'success' : 'warning'} data-id="element-3768" />
        <StatsCard title="Entregados" value={isRepartidor ? orders.filter(o => o.estado === 'entregado').length : pedidosCompletados} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-3772" />} variant="success" data-id="element-3771" />
        <StatsCard title="Monto Total" value={formatCurrency(montoTotal)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3774" />} variant="default" data-id="element-3773" />
      </div>

      {/* Repartidor Tab Filter */}
      {isRepartidor && <div className="flex items-center gap-1.5 sm:gap-2 bg-white border border-slate-200 rounded-xl p-1 sm:p-1.5 animate-stagger-1 overflow-x-auto" data-id="element-3775">
          <button onClick={() => {
        setRepartidorTab('pendientes');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'pendientes' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3776">
            <PackageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3777" />
            Por Entregar
            {repartidorPendientesCount > 0 && <span className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${repartidorTab === 'pendientes' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`} data-id="element-3778">
                {repartidorPendientesCount}
              </span>}
          </button>
          <button onClick={() => {
        setRepartidorTab('entregados');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'entregados' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3779">
            <CheckCircleIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3780" />
            Entregados
            {repartidorEntregadosCount > 0 && <span className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${repartidorTab === 'entregados' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-3781">
                {repartidorEntregadosCount}
              </span>}
          </button>
          <button onClick={() => {
        setRepartidorTab('todos');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'todos' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3782">
            <HistoryIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3783" />
            Todos
          </button>
        </div>}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 animate-stagger-2" data-id="element-3784">
        <div className="relative flex-1" data-id="element-3785">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3786" />
          <input type="text" placeholder={isRepartidor ? 'Buscar por farmacia o pedido...' : isCliente ? 'Buscar por número de pedido...' : 'Buscar por farmacia, pedido o vendedor...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-3787" />
        </div>
        {!isRepartidor && !isCliente && <>
            <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-3788">
              <option value="all" data-id="element-3789">Todos los vendedores</option>
              {vendedores.map(v => <option key={v} value={v} data-id="element-3790">
                  {v}
                </option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-3791">
              <option value="all" data-id="element-3792">Todos los estados</option>
              <option value="pendiente" data-id="element-3793">Pendiente</option>
              <option value="procesando" data-id="element-3794">Procesando</option>
              <option value="empacado" data-id="element-3795">Empacado</option>
              <option value="enviado" data-id="element-3796">Enviado</option>
              <option value="entregado" data-id="element-3797">Entregado</option>
              <option value="cancelado" data-id="element-3798">Cancelado</option>
            </select>
          </>}
      </div>

      {/* Orders list */}
      <Card padding="none" className="animate-stagger-3" data-id="element-3799">
        {filteredOrders.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-3800">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3801">
              {isRepartidor ? <TruckIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-3802" /> : <ShoppingCartIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-3803" />}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-3804">
              {isRepartidor ? repartidorTab === 'entregados' ? 'Sin entregas registradas' : repartidorTab === 'pendientes' ? 'Sin entregas pendientes' : 'Sin pedidos' : isCliente ? 'No hay pedidos para ti' : 'No hay pedidos'}
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-3805">
              {isRepartidor ? repartidorTab === 'entregados' ? 'Aún no has marcado ningún pedido como entregado.' : repartidorTab === 'pendientes' ? 'No hay pedidos empacados listos para entregar.' : 'No se encontraron pedidos.' : isCliente ? 'No se encontraron pedidos para ti.' : 'No se encontraron pedidos con los filtros aplicados'}
            </p>
          </div> : <>
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100" data-id="element-3806">
              {paginatedOrders.map(order => <div key={order.id} className="p-3.5" data-id="element-3807">
                  <div className="flex items-start justify-between gap-2" data-id="element-3808">
                    <div className="min-w-0 flex-1" data-id="element-3809">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3810">
                        <p className="font-bold text-slate-900 text-sm" data-id="element-3811">
                          {order.numeroPedido}
                        </p>
                        {isRepartidor ? getStatusBadge(order.estado) : <button onClick={() => cycleStatus(order)} className="cursor-pointer hover:opacity-75 transition-opacity" data-id="element-3812">
                            {getStatusBadge(order.estado)}
                          </button>}
                      </div>
                      <p className="font-semibold text-slate-800 text-sm mt-1" data-id="element-3813">
                        {order.farmacia}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500" data-id="element-3814">
                        <span className="flex items-center gap-1" data-id="element-3815">
                          <UserIcon className="w-3 h-3" data-id="element-3816" />
                          {order.vendedor}
                        </span>
                        <span data-id="element-3817">{formatDate(order.fecha)}</span>
                        <span data-id="element-3818">{order.items} items</span>
                      </div>
                    </div>
                    <p className="text-base font-bold text-[#2952C4] flex-shrink-0" data-id="element-3819">
                      {formatCurrency(order.total)}
                    </p>
                  </div>
                  {/* Action buttons */}
                  <div className="mt-2.5 flex items-center gap-1.5 justify-end" data-id="element-3820">
                    {savedOrderId === order.id && <span className="text-xs text-emerald-600 font-medium animate-pulse mr-auto" data-id="element-3821">
                        ✓ Guardado
                      </span>}
                    {isRepartidor ? <>
                        {(order.estado === 'empacado' || order.estado === 'enviado') && <>
                            <button onClick={() => updateStatus(order, 'entregado')} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-3822">
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-3823" />
                              Entregado
                            </button>
                            <button onClick={() => updateStatus(order, 'cancelado')} className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-3824">
                              <BanIcon className="w-3.5 h-3.5" data-id="element-3825" />
                            </button>
                          </>}
                        <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3826">
                          <EditIcon className="w-4 h-4" data-id="element-3827" />
                        </button>
                      </> : isCliente ? null : <>
                        {order.estado === 'pendiente' && <>
                            <button onClick={() => updateStatus(order, 'procesando')} title="Aprobar" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-3828">
                              <CheckIcon className="w-4 h-4" data-id="element-3829" />
                            </button>
                            <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3830">
                              <BanIcon className="w-4 h-4" data-id="element-3831" />
                            </button>
                          </>}
                        <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3832">
                          <EditIcon className="w-4 h-4" data-id="element-3833" />
                        </button>
                        <button onClick={() => {
                  setEditingOrder(order);
                  setIsDeleteModalOpen(true);
                }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3834">
                          <TrashIcon className="w-4 h-4" data-id="element-3835" />
                        </button>
                      </>}
                  </div>
                </div>)}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-3836">
              <table className="w-full" data-id="element-3837">
                <thead data-id="element-3838">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-3839">
                    <SortableTableHeader label="Pedido" sortKey="numeroPedido" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3840" />
                    <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3841" />
                    <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3842" />
                    <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3843" />
                    <SortableTableHeader label="Items" sortKey="items" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3844" />
                    <SortableTableHeader label="Total" sortKey="total" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3845" />
                    <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3846" />
                    <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase font-heading" data-id="element-3847">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3848">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-3849">
                      <td className="px-5 py-4 font-medium text-slate-900" data-id="element-3850">
                        {order.numeroPedido}
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-900" data-id="element-3851">
                        {order.farmacia}
                      </td>
                      <td className="px-5 py-4" data-id="element-3852">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-3853">
                          <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3854" />
                          {order.vendedor}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600" data-id="element-3855">
                        {formatDate(order.fecha)}
                      </td>
                      <td className="px-5 py-4 text-center text-sm text-slate-600" data-id="element-3856">
                        {order.items}
                      </td>
                      <td className="px-5 py-4 text-right font-medium text-slate-900" data-id="element-3857">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3858">
                        <div className="flex flex-col items-center gap-1" data-id="element-3859">
                          {isRepartidor ? getStatusBadge(order.estado) : <button onClick={() => cycleStatus(order)} title="Clic para cambiar estado" className="cursor-pointer hover:opacity-75 transition-opacity" data-id="element-3860">
                              {getStatusBadge(order.estado)}
                            </button>}
                          {savedOrderId === order.id && <span className="text-xs text-emerald-600 font-medium animate-pulse" data-id="element-3861">
                              ✓ Guardado
                            </span>}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-3862">
                        {isRepartidor ? <div className="flex items-center justify-end gap-1" data-id="element-3863">
                            {(order.estado === 'empacado' || order.estado === 'enviado') && <>
                                <button onClick={() => updateStatus(order, 'entregado')} title="Marcar Entregado" className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-3864">
                                  <CheckIcon className="w-3.5 h-3.5" data-id="element-3865" />
                                  Entregado
                                </button>
                                <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar Entrega" className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-3866">
                                  <BanIcon className="w-3.5 h-3.5" data-id="element-3867" />
                                  Cancelar
                                </button>
                              </>}
                            {order.estado === 'entregado' && <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-lg" data-id="element-3868">
                                <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-3869" />
                                Entregado
                              </span>}
                            {order.estado === 'cancelado' && <span className="text-xs text-rose-500 font-medium" data-id="element-3870">
                                Cancelado
                              </span>}
                            <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-3871" />
                            <button onClick={() => openEditModal(order)} title="Editar Pedido" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3872">
                              <EditIcon className="w-4 h-4" data-id="element-3873" />
                            </button>
                          </div> : isCliente ? <div className="flex items-center justify-end" data-id="element-3874">
                            {getStatusBadge(order.estado)}
                          </div> : <div className="flex items-center justify-end gap-1" data-id="element-3875">
                            {order.estado === 'pendiente' && <>
                                <button onClick={() => updateStatus(order, 'procesando')} title="Aprobar Pedido" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-3876">
                                  <CheckIcon className="w-4 h-4" data-id="element-3877" />
                                </button>
                                <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar Pedido" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3878">
                                  <BanIcon className="w-4 h-4" data-id="element-3879" />
                                </button>
                                <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-3880" />
                              </>}
                            <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3881">
                              <EditIcon className="w-4 h-4" data-id="element-3882" />
                            </button>
                            <button onClick={() => {
                      setEditingOrder(order);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3883">
                              <TrashIcon className="w-4 h-4" data-id="element-3884" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </Card>
      <div className="animate-stagger-4" data-id="element-3885">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredOrders.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3886" />
      </div>
      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingOrder ? 'Editar Pedido' : 'Nuevo Pedido'} size="xl" data-id="element-3887">
        <ModalBody data-id="element-3888">
          <div className="space-y-5" data-id="element-3889">
            {/* Header fields */}
            <div className="grid grid-cols-2 gap-4" data-id="element-3890">
              <Input label="Número de Pedido" value={headerForm.numeroPedido} onChange={e => setHeaderForm({
              ...headerForm,
              numeroPedido: e.target.value
            })} placeholder="PED-2024-001" disabled={isCliente} data-id="element-3891" />
              {!isCliente && <div data-id="element-3892">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3893">
                    Estado
                  </label>
                  <select value={headerForm.estado} onChange={e => setHeaderForm({
                ...headerForm,
                estado: e.target.value as Order['estado']
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3894">
                    <option value="pendiente" data-id="element-3895">Pendiente</option>
                    <option value="procesando" data-id="element-3896">Procesando</option>
                    <option value="empacado" data-id="element-3897">Empacado</option>
                    <option value="enviado" data-id="element-3898">Enviado</option>
                    <option value="entregado" data-id="element-3899">Entregado</option>
                    <option value="cancelado" data-id="element-3900">Cancelado</option>
                  </select>
                </div>}
            </div>
            <div className={`grid ${isCliente ? 'grid-cols-1' : 'grid-cols-3'} gap-4`} data-id="element-3901">
              {isCliente ? <Input label="Farmacia" value={headerForm.farmacia} disabled data-id="element-3902" /> : <SearchableSelect label="Farmacia" value={headerForm.farmacia} onChange={val => {
              const matchedClient = clients.find(c => c.nombre === val);
              let autoVendedor = headerForm.vendedor;
              if (matchedClient) {
                // Try exact name match first
                if (matchedClient.vendedorNombre && vendedores.includes(matchedClient.vendedorNombre)) {
                  autoVendedor = matchedClient.vendedorNombre;
                } else if (matchedClient.vendedorId) {
                  // Fallback: match by vendedorId to get current name from users
                  const vendedorUser = users.find(u => u.id === matchedClient.vendedorId);
                  if (vendedorUser && vendedores.includes(vendedorUser.nombre)) {
                    autoVendedor = vendedorUser.nombre;
                  }
                }
              }
              setHeaderForm({
                ...headerForm,
                farmacia: val,
                vendedor: autoVendedor
              });
            }} options={farmacias} placeholder="Buscar farmacia..." data-id="element-3903" />}
              {!isCliente && <div data-id="element-3904">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3905">
                    Vendedor
                  </label>
                  <select value={headerForm.vendedor} onChange={e => setHeaderForm({
                ...headerForm,
                vendedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3906">
                    <option value="" data-id="element-3907">Seleccionar vendedor...</option>
                    {vendedores.map(v => <option key={v} value={v} data-id="element-3908">
                        {v}
                      </option>)}
                  </select>
                </div>}
              {!isCliente && <Input label="Fecha" type="date" value={headerForm.fecha} onChange={e => setHeaderForm({
              ...headerForm,
              fecha: e.target.value
            })} data-id="element-3909" />}
            </div>

            {/* Error message */}
            {saveError && <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3910">
                <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-3911" />
                <div data-id="element-3912">
                  <p className="font-semibold" data-id="element-3913">Error al guardar</p>
                  <p className="mt-0.5" data-id="element-3914">{saveError}</p>
                </div>
                <button onClick={() => setSaveError(null)} className="ml-auto p-1 text-rose-400 hover:text-rose-600 rounded transition-colors flex-shrink-0" data-id="element-3915">
                  <XIcon className="w-4 h-4" data-id="element-3916" />
                </button>
              </div>}

            {/* Product search */}
            <div data-id="element-3917">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3918">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3919">
                  Agregar Producto
                </label>
                <span className="text-xs text-slate-400" data-id="element-3920">
                  {inventory.length} productos disponibles
                </span>
              </div>
              <div className="relative" data-id="element-3921">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3922" />
                <input type="text" placeholder="Escribe para buscar o deja vacío para ver todos..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-3923" />
              </div>
              {productResults.length > 0 && <div className="mt-1 border border-slate-200 rounded-xl overflow-hidden shadow-sm max-h-60 overflow-y-auto" data-id="element-3924">
                  {productResults.map(product => {
                const promo = activePromos.find(pr => pr.productId === product.id);
                return <button key={product.id} onClick={() => addProductToOrder(product)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-slate-100 last:border-0" data-id="element-3925">
                        <div className="flex items-center gap-3" data-id="element-3926">
                          <div className={`p-1.5 rounded-lg ${promo ? promo.tipo === 'oferta' ? 'bg-rose-100' : 'bg-amber-100' : 'bg-slate-100'}`} data-id="element-3927">
                            <PackageIcon className={`w-4 h-4 ${promo ? promo.tipo === 'oferta' ? 'text-rose-600' : 'text-amber-600' : 'text-slate-500'}`} data-id="element-3928" />
                          </div>
                          <div data-id="element-3929">
                            <div className="flex items-center gap-2" data-id="element-3930">
                              <p className="text-sm font-medium text-slate-900" data-id="element-3931">
                                {product.nombre}
                              </p>
                              {promo && <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${promo.tipo === 'oferta' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-3932">
                                  {promo.tipo === 'oferta' ? '🔥 Oferta' : '⭐ Impulso'}
                                </span>}
                            </div>
                            <p className="text-xs text-slate-400" data-id="element-3933">
                              {product.sku} ·{' '}
                              {formatCurrency(promo && promo.precioOferta > 0 ? promo.precioOferta : product.precio)}
                              {promo && promo.descuentoPct > 0 && <span className="ml-1 text-emerald-600 font-semibold" data-id="element-3934">
                                  (-{promo.descuentoPct}%)
                                </span>}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${product.stock === 0 ? 'bg-rose-100 text-rose-700' : product.stock <= product.stockMinimo ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-3935">
                          {product.stock === 0 ? 'Sin stock' : `${product.stock} uds`}
                        </span>
                      </button>;
              })}
                </div>}
            </div>

            {/* Line items */}
            {lineas.length > 0 && <div data-id="element-3936">
                <div className="flex items-center justify-between mb-2" data-id="element-3937">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3938">
                    Productos del Pedido
                  </label>
                  {hasStockIssues && <span className="flex items-center gap-1 text-xs font-semibold text-rose-600" data-id="element-3939">
                      <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3940" />
                      Hay productos sin stock suficiente
                    </span>}
                </div>
                <div className="border border-slate-200 rounded-xl overflow-x-auto" data-id="element-3941">
                  <table className="w-full text-sm min-w-[650px]" data-id="element-3942">
                    <thead data-id="element-3943">
                      <tr className="bg-slate-50 border-b border-slate-200" data-id="element-3944">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500" data-id="element-3945">
                          Producto
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500" data-id="element-3946">
                          Cantidad
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3947">
                          Precio
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3948">
                          Subtotal
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3949">
                          ITBIS
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500" data-id="element-3950">
                          Stock
                        </th>
                        <th className="px-4 py-2.5 w-8" data-id="element-3951"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-3952">
                      {lineas.map(linea => {
                    const indicator = getStockIndicator(linea);
                    return <tr key={linea.productoId} className={linea.cantidad > linea.stockDisponible ? 'bg-rose-50/30' : ''} data-id="element-3953">
                            <td className="px-4 py-3" data-id="element-3954">
                              <p className="font-medium text-slate-900 text-xs" data-id="element-3955">
                                {linea.nombre}
                              </p>
                              <p className="text-xs text-slate-400" data-id="element-3956">
                                {linea.sku}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3957">
                              <input type="number" min="1" value={linea.cantidad} onChange={e => updateLineCantidad(linea.productoId, Number(e.target.value))} className="w-16 text-center border border-slate-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3958" />
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700" data-id="element-3959">
                              {formatCurrency(linea.precio)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900" data-id="element-3960">
                              {formatCurrency(linea.cantidad * linea.precio)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600" data-id="element-3961">
                              {formatCurrency(linea.cantidad * (linea.itbis || 0))}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3962">
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${indicator.bg} ${indicator.color}`} data-id="element-3963">
                                {indicator.icon} {indicator.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3964">
                              <button onClick={() => removeLinea(linea.productoId)} className="p-1 text-slate-400 hover:text-rose-600 rounded transition-colors" data-id="element-3965">
                                <XIcon className="w-3.5 h-3.5" data-id="element-3966" />
                              </button>
                            </td>
                          </tr>;
                  })}
                    </tbody>
                    <tfoot data-id="element-3967">
                      <tr className="bg-slate-50 border-t border-slate-200" data-id="element-3968">
                        <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-slate-700" data-id="element-3969">
                          Total ({lineas.reduce((s, l) => s + l.cantidad, 0)}{' '}
                          unidades)
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900" data-id="element-3970">
                          {formatCurrency(orderSubtotal)}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-3971">
                          {formatCurrency(orderItbisTotal)}
                        </td>
                        <td colSpan={2} data-id="element-3972"></td>
                      </tr>
                      <tr className="bg-blue-50 border-t border-blue-200" data-id="element-3973">
                        <td colSpan={4} className="px-4 py-3 text-sm font-bold text-blue-800 text-right" data-id="element-3974">
                          Total con ITBIS
                        </td>
                        <td className="px-4 py-3 text-right font-black text-blue-900 text-base" data-id="element-3975">
                          {formatCurrency(orderTotal)}
                        </td>
                        <td colSpan={2} data-id="element-3976"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>}

            {lineas.length === 0 && <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-400" data-id="element-3977">
                <PackageIcon className="w-8 h-8 mb-2" data-id="element-3978" />
                <p className="text-sm font-medium" data-id="element-3979">
                  Busca y agrega productos al pedido
                </p>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3980">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-3981">
            Cancelar
          </Button>
          {lineas.length > 0 && <Button variant="secondary" leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-3983" />} onClick={async () => {
          // Only save cotización to DB if not already saved in this session
          if (!savedCotizacionId) {
            try {
              const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
              const cotizacionPayload = {
                numero_cotizacion: headerForm.numeroPedido,
                farmacia: headerForm.farmacia,
                vendedor: headerForm.vendedor,
                fecha: new Date(headerForm.fecha).toISOString(),
                items: totalItems,
                total: orderTotal
              };
              const {
                data: newCotizacion,
                error: cotError
              } = await supabaseAdmin.from('cotizaciones').insert(cotizacionPayload).select().single();
              if (cotError) {
                console.error('Error guardando cotización:', cotError.message, cotError.details, cotError.hint);
              }
              if (newCotizacion) {
                setSavedCotizacionId(newCotizacion.id);
                // Save cotización lines
                if (lineas.length > 0) {
                  const printCotLinesPayload = lineas.map(l => ({
                    cotizacion_id: newCotizacion.id,
                    producto_id: l.productoId,
                    nombre: l.nombre,
                    sku: l.sku,
                    cantidad: l.cantidad,
                    precio: l.precio,
                    stock_disponible: l.stockDisponible
                  }));
                  const {
                    error: linesError
                  } = await supabaseAdmin.from('cotizacion_lines').insert(printCotLinesPayload).select();
                  if (linesError) {
                    console.error('Error guardando líneas de cotización (print):', linesError.message);
                  }
                }
              }
            } catch (err) {
              console.error('Error al guardar cotización en BD:', err);
            }
          }
          // Generate PDF (always, even if DB save fails or was already saved)
          const matchedClient = clients.find(c => c.nombre === headerForm.farmacia);
          await generateCotizacionPDF({
            numeroPedido: headerForm.numeroPedido,
            farmacia: headerForm.farmacia,
            vendedor: headerForm.vendedor,
            fecha: headerForm.fecha,
            lineas,
            clientData: matchedClient || null
          });
        }} data-id="element-3982">
              Imprimir Cotización
            </Button>}
          <Button onClick={handleSave} isLoading={isSaving} disabled={!headerForm.numeroPedido || lineas.length === 0} data-id="element-3984">
            {editingOrder ? 'Guardar Cambios' : 'Crear Pedido'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Pedido" size="sm" data-id="element-3985">
        <ModalBody data-id="element-3986">
          <p className="text-slate-600" data-id="element-3987">
            ¿Eliminar el pedido <strong data-id="element-3988">{editingOrder?.numeroPedido}</strong>?
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-3989">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-3990">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-3991">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}